import type { ComponentPropsWithRef, ReactNode } from 'react';

/**
 * The skeleton `Legend` and `ChartLegend` both build by hand:
 *
 *   ul → optional title li → li → row (button | span) → mark + label + value
 *
 * What lives here is the structure and nothing else: the list's accessible
 * name, the presentational title row, the list-item per entry, the choice
 * between a real `button` and plain text, the `aria-pressed` of an interactive
 * row, and the key. Every class string is a slot the caller fills, because the
 * two legends legitimately style the same slot differently — and the caller
 * owns the item's identity (`getKey`) and its row content (`renderItem`).
 *
 * Deliberately private: it is the shared half of two components, not a third
 * one, so it is not re-exported from this folder's barrel.
 */
interface LegendListProps<Item> extends Omit<ComponentPropsWithRef<'ul'>, 'children'> {
  items: readonly Item[];
  /** The list's accessible name. A `title` wins, because it names the list too. */
  label?: string;
  /** The heading row, repeated as a presentational `li` at the head of the list. */
  title?: string;
  /** Item identity — stable data, never the index on its own. */
  getKey: (item: Item, index: number) => string;
  /** The row's content: mark, label, value. */
  renderItem: (item: Item, index: number) => ReactNode;
  /** Given, every row is a real `button`. Omitted, every row is plain text. */
  onItemActivate?: (item: Item, index: number) => void;
  /** The `aria-pressed` of an interactive row. */
  isPressed?: (item: Item, index: number) => boolean;
  /** The `<ul>` slot. */
  className?: string;
  /** The title `<li>` slot. */
  titleClassName?: string;
  /** The item `<li>` slot. */
  itemClassName?: string;
  /** The interactive row slot — per item, because its state is per item. */
  actionClassName?: (item: Item, index: number) => string;
  /** The descriptive row slot. */
  staticClassName?: string;
}

export function LegendList<Item>({
  items,
  label,
  title,
  getKey,
  renderItem,
  onItemActivate,
  isPressed,
  className,
  titleClassName,
  itemClassName,
  actionClassName,
  staticClassName,
  ...rest
}: LegendListProps<Item>) {
  return (
    <ul {...rest} aria-label={title ?? label} className={className}>
      {title ? (
        // The title names the list for assistive tech (above) and is repeated
        // visually here; role="presentation" keeps it out of the item count.
        <li role="presentation" aria-hidden="true" className={titleClassName}>
          {title}
        </li>
      ) : null}

      {items.map((item, index) => (
        <li key={getKey(item, index)} className={itemClassName}>
          {onItemActivate ? (
            <button
              type="button"
              onClick={() => onItemActivate(item, index)}
              aria-pressed={isPressed?.(item, index) ?? false}
              className={actionClassName?.(item, index)}
            >
              {renderItem(item, index)}
            </button>
          ) : (
            <span className={staticClassName}>{renderItem(item, index)}</span>
          )}
        </li>
      ))}
    </ul>
  );
}
